import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Zap, Layers, Activity, ChevronRight, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const Semiconductor = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  const technologies = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: 'Advanced Node Technology',
      description: 'Cutting-edge 3nm and 5nm process nodes for ultra-dense chip designs',
      specs: ['3nm FinFET', '5nm GAA', 'EUV Lithography'],
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: '3D Chip Stacking',
      description: 'Vertical integration technology enabling higher density and performance',
      specs: ['TSV Integration', 'Hybrid Bonding', 'Multi-die Packages'],
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Power Efficiency',
      description: 'Low-power design techniques for energy-efficient computation',
      specs: ['Dynamic Voltage', 'Clock Gating', 'Power Islands'],
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: 'High-Speed Interconnects',
      description: 'Advanced on-chip and chip-to-chip communication systems',
      specs: ['SerDes >100Gbps', 'Low-latency NoC', 'Optical I/O Ready'],
    },
  ];

  const applications = [
    {
      title: 'Quantum Control Electronics',
      description: 'Specialized semiconductor chips for precise qubit control and readout',
      impact: 'Enables scalable quantum computers with 1000+ qubits',
    },
    {
      title: 'AI Accelerators',
      description: 'Domain-specific architectures optimized for neural network inference',
      impact: 'Achieves 10x performance per watt over general-purpose processors',
    },
    {
      title: 'Edge Computing',
      description: 'Ultra-low-power chips for IoT and edge AI applications',
      impact: 'Powers billions of connected devices with minimal energy',
    },
    {
      title: 'Data Center Infrastructure',
      description: 'High-performance chips for cloud computing and storage',
      impact: 'Processes exabytes of data with maximum efficiency',
    },
  ];

  const features = [
    'Nanoscale Precision Manufacturing',
    'Advanced Packaging Solutions',
    'Design for Reliability',
    'Thermal Management Innovation',
    'Signal Integrity Optimization',
    'Test & Validation Infrastructure',
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes circuit-flow {
          0% { stroke-dashoffset: 1000; }
          100% { stroke-dashoffset: 0; }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-circuit-flow {
          animation: circuit-flow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[70vh] flex items-center ${classes.bg} overflow-hidden`}>
        {/* Circuit Board Background */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M 10 50 L 50 50 L 50 10" stroke="currentColor" strokeWidth="2" fill="none" />
                <circle cx="50" cy="50" r="5" fill="currentColor" />
                <path d="M 50 90 L 50 50 L 90 50" stroke="currentColor" strokeWidth="2" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#circuit)" />
          </svg>
        </div>

        {/* Animated Circuit Lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="circuit-gradient">
              <stop offset="0%" stopColor={theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} stopOpacity="0.5" />
              <stop offset="100%" stopColor={theme === 'light' ? '#8b5cf6' : theme === 'dark' ? '#3b82f6' : '#ec4899'} stopOpacity="0.8" />
            </linearGradient>
          </defs>
          <path
            d="M 0 50% L 50% 50% L 50% 20%"
            stroke="url(#circuit-gradient)"
            strokeWidth="2"
            fill="none"
            strokeDasharray="10 5"
            className="animate-circuit-flow"
          />
          <path
            d="M 100% 50% L 50% 50% L 50% 80%"
            stroke="url(#circuit-gradient)"
            strokeWidth="2"
            fill="none"
            strokeDasharray="10 5"
            className="animate-circuit-flow"
            style={{ animationDelay: '1s' }}
          />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-up">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
                Semiconductor{' '}
                <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                  Excellence
                </span>
              </h1>
              <p className={`text-lg sm:text-xl ${classes.subtext} mb-8`}>
                Engineering the future of computing with nanoscale precision. Our advanced semiconductor
                technology powers quantum processors, AI accelerators, and next-generation data centers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
                >
                  Partner With Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/research"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                    theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' :
                    theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' :
                    'border-purple-500 text-purple-400 hover:bg-purple-950'
                  } transition-all`}
                >
                  View Research
                </Link>
              </div>
            </div>

            <div className="relative h-64 sm:h-96 lg:h-[400px] flex items-center justify-center animate-fade-in">
              {/* Chip Visualization */}
              <div className="relative w-full h-full">
                <div className={`absolute inset-0 flex items-center justify-center`}>
                  {/* Central Chip */}
                  <div className={`w-48 h-48 sm:w-64 sm:h-64 ${classes.cardBg} rounded-lg border-4 ${classes.cardBorder} shadow-2xl relative overflow-hidden`}>
                    {/* Grid Pattern */}
                    <div className="absolute inset-0 grid grid-cols-8 grid-rows-8 gap-1 p-4">
                      {[...Array(64)].map((_, i) => (
                        <div
                          key={i}
                          className={`${theme === 'light' ? 'bg-blue-100' : theme === 'dark' ? 'bg-cyan-900/30' : 'bg-purple-900/30'} rounded-sm`}
                          style={{
                            animationDelay: `${i * 0.02}s`,
                            animation: 'pulse 2s ease-in-out infinite',
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Central Core */}
                    <div className={`absolute inset-0 flex items-center justify-center`}>
                      <div className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-r ${classes.accent} opacity-20 blur-xl animate-pulse`} />
                      <Cpu className={`absolute w-16 h-16 sm:w-20 sm:h-20 ${classes.accentText}`} />
                    </div>
                  </div>

                  {/* Corner Connectors */}
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className={`absolute w-8 h-8 sm:w-12 sm:h-12 ${classes.cardBg} border-2 ${classes.cardBorder} rounded-full`}
                      style={{
                        [i === 0 || i === 1 ? 'top' : 'bottom']: '10%',
                        [i === 0 || i === 2 ? 'left' : 'right']: '10%',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Core Technologies
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Industry-leading semiconductor innovations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} hover:border-opacity-50 transition-all animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-6`}>
                  {tech.icon}
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${classes.text} mb-3`}>{tech.title}</h3>
                <p className={`${classes.subtext} mb-6 text-sm sm:text-base`}>{tech.description}</p>
                <div className="flex flex-wrap gap-2">
                  {tech.specs.map((spec, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1 text-xs sm:text-sm rounded-full ${classes.cardBg} border ${classes.cardBorder} ${classes.accentText}`}
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Applications
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Empowering diverse industries with advanced silicon
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} hover:border-opacity-50 transition-all animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className={`text-xl font-bold ${classes.text} mb-3`}>{app.title}</h3>
                <p className={`${classes.subtext} mb-4 text-sm`}>{app.description}</p>
                <div className={`flex items-start gap-2 ${classes.accentText} text-sm`}>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{app.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Manufacturing Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 ${classes.cardBg} backdrop-blur-md rounded-xl p-4 sm:p-6 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CheckCircle className={`w-6 h-6 ${classes.accentText} flex-shrink-0`} />
                <span className={`${classes.text} text-sm sm:text-base font-medium`}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Ready to Innovate?
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Partner with us to bring your semiconductor vision to life
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
          >
            Schedule Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Semiconductor;